import type { LearnerProfile, Completion, UnitMapping, GapRow, CoverageCell, Taxonomy, TaxonomyUnit } from "./types";
import { ROLE_PROFILES } from "./constants";

export function flattenTaxonomyUnits(taxonomy: Taxonomy): TaxonomyUnit[] {
  return taxonomy.processes.flatMap((p) =>
    p.qualifications.flatMap((q) => q.units)
  );
}

export function deriveStatus(expiryDate: string | null | undefined): "active" | "expired" | "expiring_soon" {
  if (!expiryDate) return "active";
  const today = new Date();
  const expiry = new Date(expiryDate);
  const daysUntilExpiry = (expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
  if (daysUntilExpiry < 0) return "expired";
  if (daysUntilExpiry < 90) return "expiring_soon";
  return "active";
}

export function parseCSV(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.trim().split("\n");
  const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
  const rows = lines
    .slice(1)
    .filter((l) => l.trim())
    .map((l) => l.split(",").map((c) => c.trim().replace(/^"|"$/g, "")));
  return { headers, rows };
}

export function rowToProfile(
  row: string[],
  headers: string[],
  mapping: Record<string, string>,
  index: number
): LearnerProfile {
  const get = (field: string) => {
    const col = Object.entries(mapping).find(([, v]) => v === field)?.[0];
    return col ? (row[headers.indexOf(col)] || "") : "";
  };
  const email = get("email");
  return {
    id: `CSV-${Date.now()}-${index}`,
    name: get("name") || `Imported learner ${index + 1}`,
    employeeId: get("employeeId"),
    ...(email ? { email } : {}),
    site: get("site"),
    role: get("role"),
    source: "excel",
    importedAt: new Date().toISOString().split("T")[0],
    completions: [
      {
        id: `CC-${Date.now()}-${index}`,
        rawCourseName: get("courseName"),
        normalizedCourseName: get("courseName"),
        completionDate: get("completionDate"),
        expiryDate: get("expiryDate") || null,
        issuingBody: get("issuingBody"),
        score: parseFloat(get("score")) || null,
        status: deriveStatus(get("expiryDate")),
        mappedUnits: [],
      },
    ],
  };
}

export function talentLMSUserToProfile(user: any): LearnerProfile {
  return {
    id: `TLM-${user.id}-${Date.now()}`,
    name: user.name,
    employeeId: `TLM-${user.id}`,
    site: user.custom_field_1 || "",
    role: user.role || "",
    source: "talentlms",
    importedAt: new Date().toISOString().split("T")[0],
    completions: (user.completions || []).map((c: any, i: number) => ({
      id: `TCC-${user.id}-${i}`,
      rawCourseName: c.course_name,
      normalizedCourseName: c.course_name,
      completionDate: c.completion_date,
      expiryDate: c.expiry_date || null,
      issuingBody: "TalentLMS",
      score: c.score || null,
      status: deriveStatus(c.expiry_date || null),
      mappedUnits: [],
    })),
  };
}

export function confidenceRank(c: string): number {
  return ({ HIGH: 3, MEDIUM: 2, LOW: 1 } as Record<string, number>)[c] || 0;
}

export function getLearnerUnitMap(
  learner: LearnerProfile
): Record<string, UnitMapping & { courseName: string }> {
  const map: Record<string, UnitMapping & { courseName: string }> = {};
  (learner.completions || []).forEach((c) => {
    (c.mappedUnits || []).forEach((mu) => {
      if (!map[mu.unitId] || confidenceRank(mu.confidence) > confidenceRank(map[mu.unitId].confidence)) {
        map[mu.unitId] = { ...mu, courseName: c.normalizedCourseName };
      }
    });
  });
  return map;
}

export function buildGapGrid(learners: LearnerProfile[], role: string): GapRow[] {
  const required = ROLE_PROFILES[role] || [];
  return learners.map((l) => {
    const unitMap = getLearnerUnitMap(l);
    return {
      learner: l,
      coverage: required.map((uid) => ({
        unitId: uid,
        covered: !!unitMap[uid],
        confidence: unitMap[uid]?.confidence || null,
      })) as CoverageCell[],
    };
  });
}

export function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}
